import { PageController } from '@client/components/_core/layout/PageController';
import { TableToolbar } from '@client/components/_core/table/TableToolbar';
import { Button } from '@client/components/_ui/data-entry/ButtonModern';
import { SearchInput } from '@client/components/_ui/data-entry/SearchInput';
import { InlineQueryError } from '@client/components/_ui/feedback/InlineQueryError';
import { Space } from '@client/components/_ui/layout/Space';
import { ReadOnlyBanner } from '@client/components/admin/ReadOnlyBanner';
import { useAdminModulePermissions } from '@client/hooks/admin/useAdminModulePermissions';
import { useTrpc } from '@client/hooks/api/useTrpc';
import { useNavigate } from '@client/hooks/navigation/useNavigate';
import { usePathState } from '@client/hooks/navigation/usePathState';
import { createColumnHelper, PrismaTable, usePrismaTable } from '@client/hooks/table-v2';
import { useFormatter } from '@client/hooks/utils/useFormatter';
import type { RouterInputs, RouterOutputs } from '@client/trpc/client';
import { DEFAULT_PAGE_SIZE } from '@shared/constants';
import { createFileRoute } from '@tanstack/react-router';
import { Select, Tag } from 'antd';
import { Megaphone } from 'lucide-react';

export const Route = createFileRoute('/admin-console/platform-content/whats-new/')({
    staticData: {
        presentation: 'fullPage',
        ui: {
            icon: Megaphone,
            title: "What's new",
            subtitle: 'Release announcements shown to tenant users, newest first',
            widthVariant: 'fullWidth',
        },
    },
    component: WhatsNewReleaseList,
});

type QueryParams = RouterInputs['admin']['whatsNew']['listReleases'];
type ListData = RouterOutputs['admin']['whatsNew']['listReleases'];
type Row = ListData['rows'][number];
type OrderBy = NonNullable<QueryParams['orderBy']>;

const STATUS_COLOURS: Record<Row['status'], string> = { DRAFT: 'default', PUBLISHED: 'green', ARCHIVED: 'orange' };
const STATUS_LABELS: Record<Row['status'], string> = { DRAFT: 'Draft', PUBLISHED: 'Published', ARCHIVED: 'Archived' };
const STATUS_OPTIONS = (Object.keys(STATUS_LABELS) as Row['status'][]).map((value) => ({
    value,
    label: STATUS_LABELS[value],
}));

function WhatsNewReleaseList() {
    const { trpc } = useTrpc();
    const navigate = useNavigate();
    const { formatISODate, formatDateTime } = useFormatter();
    const { isReadOnly } = useAdminModulePermissions('content');

    const [input, setInput] = usePathState<QueryParams>('query-params', {
        limit: DEFAULT_PAGE_SIZE,
        offset: 0,
        orderBy: { releaseDate: 'desc' },
    });

    const { data, isPending, error, refetch } = trpc.admin.whatsNew.listReleases.useQuery(input);

    const columnHelper = createColumnHelper<Row, OrderBy>();
    const columns = [
        columnHelper.accessor('title', {
            id: 'title',
            header: 'Title',
            cell: ({ row }) => (
                <div className="flex flex-col">
                    <span className="font-medium">{row.title}</span>
                    <span className="text-subtle-foreground text-xs">{row.slug}</span>
                </div>
            ),
        }),
        columnHelper.accessor('status', {
            id: 'status',
            header: 'Status',
            cell: ({ row }) => (
                <Tag variant="outlined" color={STATUS_COLOURS[row.status]}>
                    {STATUS_LABELS[row.status]}
                </Tag>
            ),
            className: 'whitespace-nowrap',
        }),
        columnHelper.display({
            id: 'audience',
            header: 'Audience',
            cell: ({ row }) =>
                row.audience === 'ALL'
                    ? 'Everyone'
                    : row.audienceRoles.map((role) => role.replace(/_/g, ' ')).join(', '),
        }),
        columnHelper.accessor('releaseDate', {
            id: 'releaseDate',
            header: 'Release date',
            cell: ({ row }) => formatISODate(row.releaseDate.toISOString().slice(0, 10)),
            className: 'whitespace-nowrap',
        }),
        columnHelper.accessor('publishedAt', {
            id: 'publishedAt',
            header: 'Published',
            cell: ({ row }) => (row.publishedAt ? formatDateTime(row.publishedAt) : '-'),
            className: 'whitespace-nowrap',
        }),
        columnHelper.display({
            id: 'modifiedBy',
            header: 'Last edited',
            cell: ({ row }) => {
                const editorName = [row.modifiedBy?.firstName, row.modifiedBy?.lastName].filter(Boolean).join(' ');
                return (
                    <span className="whitespace-nowrap">
                        {formatDateTime(row.updatedAt)}
                        {editorName && <span className="text-subtle-foreground"> · {editorName}</span>}
                    </span>
                );
            },
        }),
    ];

    const tableProps = usePrismaTable<Row, undefined, OrderBy>({
        data: data?.rows ?? [],
        total: data?.total ?? 0,
        loading: isPending,
        columns,
        rowKey: 'id',
        queryParams: input,
        onParamsChange: setInput,
    });

    return (
        <PageController route={Route}>
            <ReadOnlyBanner module="content" />
            <TableToolbar
                filters={
                    <Space size="small">
                        <SearchInput
                            navigateOnSearch={false}
                            initialValue={input.search ?? ''}
                            onSearch={(value) =>
                                setInput((prev) => ({ ...prev, offset: 0, search: value || undefined }))
                            }
                        />
                        <Select
                            allowClear
                            placeholder="Any status"
                            options={STATUS_OPTIONS}
                            value={input.status}
                            className="w-40"
                            onChange={(status) => setInput((prev) => ({ ...prev, offset: 0, status }))}
                        />
                    </Space>
                }
                actions={
                    !isReadOnly ? (
                        <Button.Add.Link
                            to="/admin-console/platform-content/whats-new/new"
                            variant="primary"
                            label="New release"
                        />
                    ) : undefined
                }
            />
            {error ? (
                <InlineQueryError error={error} onRetry={() => void refetch()} title="Couldn't load releases" />
            ) : (
                <PrismaTable
                    {...tableProps}
                    scroll={{ x: 'max-content' }}
                    onRow={(record) => ({
                        className: 'cursor-pointer',
                        onClick(event) {
                            navigate(
                                {
                                    to: '/admin-console/platform-content/whats-new/$id',
                                    params: { id: String(record.id) },
                                },
                                event,
                            );
                        },
                    })}
                />
            )}
        </PageController>
    );
}
